import {
  Order,
  OrderDeal,
  OrderDealOrderModifierRow,
  OrderMenuItem,
  OrderMenuItemExtra,
  OrderMenuItemModifier,
  OrderMenuItemModifierType,
  OrderMenuItemVariationOption,
} from './order';
import { normalizeMenuItemsOrderForUpgradeBundles } from './order-menu-item-line-price.util';
import {
  collectDealCatalogMenuItemIds,
  dealOrderUsesLegacyModifierMatching,
  resolveDealOrderLineIndex,
  resolveDealOrderModifierMenuItemId,
} from './deal-order-slot-modifiers.util';

type DealOrderVariationJoinRow = Readonly<{
  id?: string;
  menu_item_id: string;
  variation_option_id: string | null | undefined;
  price: number;
  line_index?: number;
  menuItem?: OrderMenuItem['menuItem'];
  option?: OrderMenuItemVariationOption['option'];
}>;

type DealOrderExtraJoinRow = Readonly<{
  id?: string;
  menu_item_id: string;
  price: number;
  line_index?: number;
  menuItem?: OrderMenuItem['menuItem'];
  extra?: OrderMenuItemExtra['extra'];
}>;

type DealOrderModifierJoinRow = OrderDealOrderModifierRow & {
  option: NonNullable<OrderDealOrderModifierRow['option']>;
};

function isDealOrderModifierJoinRow(
  row: OrderDealOrderModifierRow
): row is DealOrderModifierJoinRow {
  return row.option != null;
}

function isDealOrderExtraJoinRow(
  row: DealOrderExtraJoinRow
): row is DealOrderExtraJoinRow & { extra: NonNullable<DealOrderExtraJoinRow['extra']> } {
  return row.extra != null;
}

function matchesDealSlotRow<T extends { menu_item_id: string; line_index?: number }>(
  row: T,
  menuItemId: string,
  lineIndex: number,
  useLegacyMatching: boolean
): boolean {
  if (row.menu_item_id !== menuItemId) {
    return false;
  }

  if (useLegacyMatching) {
    return true;
  }

  return resolveDealOrderLineIndex(row) === lineIndex;
}

function matchesDealModifierRow(
  row: OrderDealOrderModifierRow,
  menuItemId: string,
  lineIndex: number,
  useLegacyMatching: boolean
): boolean {
  const rowMenuItemId = resolveDealOrderModifierMenuItemId(row);
  if (!rowMenuItemId || rowMenuItemId !== menuItemId) {
    return false;
  }

  if (useLegacyMatching) {
    return true;
  }

  return resolveDealOrderLineIndex(row) === lineIndex;
}

function mapDealModifierRows(
  modifierRows: OrderDealOrderModifierRow[],
  menuItemId: string,
  lineIndex: number,
  useLegacyMatching: boolean
): OrderMenuItemModifier[] {
  return modifierRows
    .filter(isDealOrderModifierJoinRow)
    .filter((row) => matchesDealModifierRow(row, menuItemId, lineIndex, useLegacyMatching))
    .map((row) => ({
      id: row.option.id,
      type: OrderMenuItemModifierType.MODIFIER,
      price: row.price ?? 0,
      quantity: row.quantity ?? 1,
      option: row.option,
    }));
}

function buildDealMenuItemFromSlot(
  deal: OrderDeal,
  menuItemData: OrderMenuItem['menuItem'],
  menuItemId: string,
  lineIndex: number,
  variationRows: DealOrderVariationJoinRow[],
  extraRows: DealOrderExtraJoinRow[],
  modifierRows: OrderDealOrderModifierRow[],
  useLegacyMatching: boolean
): OrderMenuItem {
  const itemVariations = variationRows.filter((row) =>
    matchesDealSlotRow(row, menuItemId, lineIndex, useLegacyMatching)
  );
  const itemExtras = extraRows.filter((row) =>
    matchesDealSlotRow(row, menuItemId, lineIndex, useLegacyMatching)
  );
  const baseVariationRow = itemVariations.find((row) => row.variation_option_id == null);

  return {
    seatNumber: deal.seatNumber,
    courseId: deal.courseId,
    id: baseVariationRow?.id ?? itemVariations[0]?.id ?? itemExtras[0]?.id,
    deal: deal.deal
      ? {
          id: deal.deal.id,
          translations: deal.deal.translations,
          images: deal.deal.images,
        }
      : undefined,
    menuItem: menuItemData,
    note: null,
    quantity: 1,
    price: baseVariationRow?.price ?? itemVariations[0]?.price ?? 0,
    variation_options: itemVariations
      .filter((row) => row.variation_option_id != null && row.option)
      .map((row) => ({
        id: row.id ?? '',
        menu_item_order_id: row.id ?? '',
        variation_option_id: row.variation_option_id ?? '',
        price: row.price,
        option: row.option!,
      })),
    extras: itemExtras.filter(isDealOrderExtraJoinRow).map((row) => ({
      extra: row.extra,
      price: row.price,
    })),
    modifiers: mapDealModifierRows(modifierRows, menuItemId, lineIndex, useLegacyMatching),
    discounts: [],
  };
}

function getDealJoinRows(deal: OrderDeal): {
  variationRows: DealOrderVariationJoinRow[];
  extraRows: DealOrderExtraJoinRow[];
  modifierRows: OrderDealOrderModifierRow[];
} {
  return {
    variationRows: (deal.variations ?? []) as DealOrderVariationJoinRow[],
    extraRows: (deal.extras ?? []) as DealOrderExtraJoinRow[],
    modifierRows: deal.modifiers ?? [],
  };
}

function buildDealMenuItemsFromConfiguredSlots(deal: OrderDeal): OrderMenuItem[] | null {
  const configuredDealItems = [...(deal.deal?.items ?? [])].sort(
    (firstItem, secondItem) => (firstItem.position ?? 0) - (secondItem.position ?? 0)
  );
  if (configuredDealItems.length === 0) {
    return null;
  }

  const { variationRows, extraRows, modifierRows } = getDealJoinRows(deal);
  const useLegacyMatching = dealOrderUsesLegacyModifierMatching(
    modifierRows,
    collectDealCatalogMenuItemIds(configuredDealItems)
  );

  return configuredDealItems
    .map((catalogItem, arrayIndex) => {
      const menuItemId = catalogItem.menuItem?.id;
      if (!menuItemId || !catalogItem.menuItem) {
        return null;
      }

      const lineIndex = catalogItem.position ?? arrayIndex;

      return buildDealMenuItemFromSlot(
        deal,
        catalogItem.menuItem,
        menuItemId,
        lineIndex,
        variationRows,
        extraRows,
        modifierRows,
        useLegacyMatching
      );
    })
    .filter((item): item is OrderMenuItem => item !== null);
}

function buildDealMenuItemsLegacy(deal: OrderDeal): OrderMenuItem[] {
  const { variationRows, extraRows, modifierRows } = getDealJoinRows(deal);
  const configuredDealItems = deal.deal?.items ?? [];
  const useLegacyMatching = dealOrderUsesLegacyModifierMatching(
    modifierRows,
    collectDealCatalogMenuItemIds(configuredDealItems)
  );

  const menuItemIds = new Set<string>([
    ...variationRows.map((row) => row.menu_item_id),
    ...extraRows.map((row) => row.menu_item_id),
    ...modifierRows.map((row) => row.menu_item_id),
    ...configuredDealItems.map((row) => row.menuItem?.id).filter((id): id is string => Boolean(id)),
  ]);

  return Array.from(menuItemIds)
    .map((menuItemId) => {
      const menuItemData =
        variationRows.find((row) => row.menu_item_id === menuItemId && row.menuItem)?.menuItem ||
        extraRows.find((row) => row.menu_item_id === menuItemId && row.menuItem)?.menuItem ||
        modifierRows.find((row) => row.menu_item_id === menuItemId && row.menuItem)?.menuItem ||
        configuredDealItems.find((row) => row.menuItem?.id === menuItemId)?.menuItem;
      if (!menuItemData) {
        return null;
      }

      return buildDealMenuItemFromSlot(
        deal,
        menuItemData,
        menuItemId,
        0,
        variationRows,
        extraRows,
        modifierRows,
        useLegacyMatching
      );
    })
    .filter((item): item is OrderMenuItem => item !== null);
}

export function normalizeDealsOnOrders(orders: Order[]): Order[] {
  return orders.map((order) => ({
    ...order,
    menuItems: normalizeMenuItemsOrderForUpgradeBundles(order.menuItems ?? []),
    deals: (order.deals ?? []).map((deal) => ({
      ...deal,
      menuItems: buildDealMenuItemsFromConfiguredSlots(deal) ?? buildDealMenuItemsLegacy(deal),
    })),
  }));
}
